import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, NavLink, Navigate } from 'react-router-dom';
import './App.css';
import Login from './Login';
import SettlementInput from './SettlementInput';
import DepositTable from './DepositTable';
import UserManagement from './UserManagement';
import CompanyManagement from './CompanyManagement';
import MonthlyFeeManagement from './MonthlyFeeManagement';
import ReserveFundsManagement from './ReserveFundsManagement';
import FAQManagement from './FAQManagement';
import MemoManagement from './MemoManagement';
import HierarchyView from './HierarchyView';
import UserGuide from './UserGuide';
import SuperDashboard from './SuperDashboard';
import { clearSession } from './api';

/**
 * 저장된 JWT가 아직 유효한지 확인한다.
 * 토큰 "존재 여부"만 보던 예전 방식은 만료 후에도 로그인 화면을 계속 보여줬다.
 */
function isTokenValid(token) {
  if (!token) return false;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    if (!payload?.exp) return true; // 만료 정보가 없으면 서버 검증에 맡긴다
    return payload.exp * 1000 > Date.now();
  } catch {
    return false;
  }
}

function readStoredSession() {
  const token = localStorage.getItem('token');
  if (!isTokenValid(token)) {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    return null;
  }
  try {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  } catch {
    localStorage.removeItem('user');
    return null;
  }
}

function AppContent() {
  const [user, setUser] = useState(readStoredSession);
  const isLoggedIn = !!user;

  // 로그인 처리 함수
  const handleLogin = async (loginData) => {
    setUser(loginData.user);
  };

  // 로그아웃 처리 함수
  const handleLogout = () => {
    clearSession();
    setUser(null);
    window.location.href = '/';
  };

  // 탭이 떠 있는 동안 토큰이 만료되면 자동으로 로그인 화면으로 되돌린다.
  useEffect(() => {
    const timer = setInterval(() => {
      if (!isTokenValid(localStorage.getItem('token'))) {
        clearSession();
        setUser(null);
      }
    }, 60 * 1000);
    return () => clearInterval(timer);
  }, []);

  // 로그인되지 않은 경우 로그인 페이지 표시
  if (!isLoggedIn) {
    return <Login onLogin={handleLogin} />;
  }

  const isAdminRole = (role) => ['super', 'admin'].includes(role);
  const adminOnly = isAdminRole(user?.role);
  const superOnly = user?.role === 'super';

  return (
    <div className="admin-layout">
      <aside className="sidebar">
        <div className="sidebar-header">
          <div className="sidebar-title">관리자 페이지</div>
          <div className="user-info">
            <span>{user?.company_name || user?.username || '사용자'}</span>
            <button onClick={handleLogout} className="logout-button">로그아웃</button>
          </div>
        </div>
        <nav>
          <ul>
            {superOnly && (
              <li>
                <NavLink to="/stats" className={({ isActive }) => isActive ? 'active' : ''}>전체 통계</NavLink>
              </li>
            )}
            {adminOnly && (
              <li>
                <NavLink to="/" end className={({ isActive }) => isActive ? 'active' : ''}>정산 입력</NavLink>
              </li>
            )}
            <li>
              <NavLink to="/deposits" className={({ isActive }) => isActive ? 'active' : ''}>
                일별정산
              </NavLink>
            </li>
            <li>
              <NavLink to="/hierarchy" className={({ isActive }) => isActive ? 'active' : ''}>
                사용자 계보도
              </NavLink>
            </li>
            {adminOnly && (
              <li>
                <NavLink to="/users" className={({ isActive }) => isActive ? 'active' : ''}>사용자 및 요율 관리</NavLink>
                <NavLink to="/companies" className={({ isActive }) => isActive ? 'active' : ''}>분류 관리</NavLink>
                <NavLink to="/monthly-fees" className={({ isActive }) => isActive ? 'active' : ''}>월 이용료 정산</NavLink>
                <NavLink to="/reserve-funds" className={({ isActive }) => isActive ? 'active' : ''}>유보금 입/출금 내역</NavLink>
                <NavLink to="/faq" className={({ isActive }) => isActive ? 'active' : ''}>FAQ 관리</NavLink>
                <NavLink to="/memos" className={({ isActive }) => isActive ? 'active' : ''}>업무 메모</NavLink>
              </li>
            )}
            <li>
              <NavLink to="/guide" className={({ isActive }) => isActive ? 'active' : ''}>이용 가이드</NavLink>
            </li>
          </ul>
        </nav>
      </aside>
      <main className="main-content">
        <Routes>
          <Route path="/" element={adminOnly ? <SettlementInput /> : <Navigate to="/deposits" replace />} />
          <Route path="/stats" element={superOnly ? <SuperDashboard /> : <Navigate to={adminOnly ? "/" : "/deposits"} replace />} />
          <Route path="/deposits" element={<DepositTable />} />
          <Route path="/hierarchy" element={<HierarchyView />} />
          <Route path="/users" element={adminOnly ? <UserManagement /> : <Navigate to="/deposits" replace />} />
          <Route path="/companies" element={adminOnly ? <CompanyManagement /> : <Navigate to="/deposits" replace />} />
          <Route path="/monthly-fees" element={adminOnly ? <MonthlyFeeManagement /> : <Navigate to="/deposits" replace />} />
          <Route path="/reserve-funds" element={adminOnly ? <ReserveFundsManagement /> : <Navigate to="/deposits" replace />} />
          <Route path="/faq" element={adminOnly ? <FAQManagement /> : <Navigate to="/deposits" replace />} />
          <Route path="/memos" element={adminOnly ? <MemoManagement /> : <Navigate to="/deposits" replace />} />
          <Route path="/guide" element={<UserGuide />} />
          <Route path="*" element={<Navigate to={adminOnly ? "/" : "/deposits"} replace />} />
        </Routes>
      </main>
    </div>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App; 